import { sanitizeBody } from "modules/patient/helper/progressNoteFormatters";

/**
 * Wire <-> UI mapping for the health plan (bridge v4 shapes, see APIConfig).
 *
 * The plan object: { id, current_level: "Level N", data, patientId, published,
 * createdBy, updatedBy, updatedByName, publishedBy, publishedById, createdAt,
 * updatedAt }. `data` is keyed by category id → { items: [...], guidelines: [...] }.
 * An item is a SECTION here (e.g. "Throughout the day"): { itemId, name,
 * description, image, time, options: [...] }. An option: { name, description
 * (HTML), images: [], auto?, recipes?: [...] }.
 */

/** "Level 25" -> 25. Absent/odd strings fall back to 1 (bridge parses the same). */
export const levelNumber = (currentLevel) => {
  const n = parseInt(String(currentLevel || "").split(" ")[1], 10);
  return isNaN(n) ? 1 : n;
};

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Every plan timestamp is shown in IST, whoever is looking. */
const IST = "Asia/Kolkata";

/**
 * Plan timestamps are epoch strings/numbers, SECONDS when 10 digits (bridge
 * multiplies by 1000 — getDate, NewPatientHealthPlan.js).
 *
 * → "02 Apr 2026, 1:45 pm" (Figma 3187:6442). Three things the previous
 * version had wrong, all visible in the version-history dialog:
 *   · DAY FIRST. It read "Apr 02, 2026".
 *   · A COMMA before the time, not a space.
 *   · The hour is NOT zero-padded — "1:45 pm", not "01:45 pm". Minutes still
 *     are, so 11:00 keeps both digits naturally.
 *
 * And it rendered in the VIEWER'S timezone. These stamps say when a clinician
 * saved a plan; read from another timezone the same save reads as a different
 * hour, and there is nothing on screen to say which zone it is in. Asia/Kolkata
 * is pinned so the string means one thing everywhere.
 */
export const formatPlanDate = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "";
  let n = typeof raw === "string" ? parseInt(raw, 10) : raw;
  if (isNaN(n)) return "";
  if (String(Math.abs(n)).length === 10) n *= 1000;
  const d = new Date(n);
  if (isNaN(d.getTime())) return "";

  try {
    /* formatToParts, not format(): the locale decides the separators otherwise,
       and this layout is the board's, not en-GB's. */
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: IST,
      day: "2-digit",
      /* NUMERIC, then indexed into MONTHS above. en-GB renders September as
         "Sept" on current ICU — four letters — which disagrees with every other
         date in this app and with the board. Taking the name from our own array
         makes the output independent of the engine's CLDR version. */
      month: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      /* hourCycle, NOT hour12. en-GB resolves `hour12: true` to the h11 cycle
         (0-11), so midnight formatted as "0:00 am" and noon as "0:00 pm". h12
         is the 1-12 clock the board shows. Note the two options conflict — if
         hour12 is also present it wins — so it is gone rather than kept. */
      hourCycle: "h12",
    }).formatToParts(d);
    const get = (type) => (parts.find((x) => x.type === type) || {}).value || "";
    const day = get("day");
    const month = MONTHS[parseInt(get("month"), 10) - 1];
    const year = get("year");
    const hour = get("hour");
    const minute = get("minute");
    /* ICU renders this "am"/"pm" on some builds and "AM"/"PM" on others, and
       narrow forms add periods. Normalise rather than trust it. */
    const period = get("dayPeriod").toLowerCase().replace(/[^a-z]/g, "");
    if (day && month && year && hour && minute) {
      return `${day} ${month} ${year}, ${hour}:${minute} ${period}`;
    }
  } catch (e) {
    /* Fall through: an engine without full tz data must still render a stamp
       rather than throw inside a render. */
  }

  /* LOCAL fallback, same shape. Only reachable if Intl has no Asia/Kolkata. */
  const h24 = d.getHours();
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${h}:${String(d.getMinutes()).padStart(2, "0")} ${h24 < 12 ? "am" : "pm"}`;
};

/** Bridge's meal-time rule (timeFormat.js): "h:mm am/pm", empty allowed.
 *  Lower-case am/pm only, as bridge's — "9:00 AM" does not pass. */
export const isTimeValid = (time) => {
  const t = String(time || "").trim();
  if (!t) return true;
  return /^((1[0-2]|0?[1-9]):([0-5][0-9]) ?([ap]m))$/.test(t);
};

/** Bridge's guideline units (Guidelines.js) — a fixed list, NOT the
 *  ingredient units a recipe uses. */
export const GUIDELINE_UNITS = ["Litres", "Grams", "Tsp per meal", "Tbsp per day"];

/** Option/guideline descriptions are stored HTML — never inject them raw. */
export const descHtml = (html) => sanitizeBody(html);

/** An empty data entry for every category, bridge's defensive default. */
export const emptyData = (categories) => {
  const data = {};
  (categories || []).forEach((c) => {
    data[c.id] = { items: [], guidelines: [] };
  });
  return data;
};

/** A brand-new (never saved) plan skeleton — bridge's addNewLevel shape,
 *  numeric `level` included (the create endpoint 400s without it). Always
 *  Version 3: bridge's screen never starts below it ("Generate Level 3", and
 *  "Add New" from a Level 0-2 plan). */
export const newPlanSkeleton = (patientId, categories) => ({
  id: null,
  level: 3,
  current_level: "Level 3",
  patientId,
  published: false,
  createdAt: String(Date.now()),
  updatedAt: Date.now(),
  data: emptyData(categories),
});

/**
 * Bridge's addNewLevel (newIndex.js): the version that follows `prev`. Below
 * Version 3 it is a NEW, EMPTY Version 3 (nothing carried); from Version 3 up
 * it is N+1 carrying the data forward. Unsaved (id null) until saved.
 */
export const nextVersionPlan = (prev, categories) => {
  const n = levelNumber(prev && prev.current_level);
  if (!(n >= 3)) return newPlanSkeleton(prev && prev.patientId, categories);
  return {
    ...prev,
    id: null,
    published: false,
    level: n + 1, // create 400s without the numeric level
    current_level: `Level ${n + 1}`,
    createdAt: String(Date.now()),
  };
};

/**
 * Bridge's setPlanToView: a section template's sections for one category, as
 * plan items (the template item's `id` is the item id). `null` when the
 * template has nothing for that category.
 */
export const templateSections = (template, categoryId) => {
  const raw = template && template.data && template.data[categoryId];
  const list = Array.isArray(raw) ? raw : raw && Array.isArray(raw.items) ? raw.items : null;
  if (!list) return null;
  return list.filter(Boolean).map((e) => ({
    itemId: e.itemId || e.id,
    name: e.name,
    options: e.options || [],
    time: e.time || "",
  }));
};

/**
 * Ensure every category id exists in plan.data (bridge getPatientHealthPlan
 * does exactly this so the tab never crashes on a sparse plan).
 */
export const normalizePlan = (plan, categories) => {
  const next = { ...plan, data: { ...(plan.data || {}) } };
  (categories || []).forEach((c) => {
    if (!next.data[c.id]) next.data[c.id] = { items: [], guidelines: [] };
    if (!next.data[c.id].items) next.data[c.id] = { ...next.data[c.id], items: [] };
    if (!next.data[c.id].guidelines) {
      next.data[c.id] = { ...next.data[c.id], guidelines: [] };
    }
  });
  return next;
};

/**
 * Bridge's dataFilter (newIndex.js), verbatim semantics: drop categories with
 * no items, trim each item's time, and strip the `category` field the fetch
 * embeds — the server must not get it back.
 */
export const dataFilter = (data) => {
  const out = {};
  Object.keys(data || {}).forEach((catId) => {
    const cat = data[catId] || {};
    const items = cat.items || [];
    if (!items.length) return;
    out[catId] = {
      ...cat,
      items: items.map((it) => {
        const next = { ...it, time: String(it.time || "").trim() };
        delete next.category;
        return next;
      }),
    };
    // normalizePlan seeds guidelines:[] on every category so the UI never
    // crashes; the wire (and bridge's accepted body) omits the key when there
    // are none — don't send back what we invented.
    if (out[catId].guidelines && !out[catId].guidelines.length) {
      delete out[catId].guidelines;
    }
  });
  return out;
};

/**
 * Bridge's pre-save validations (NewPatientHealthPlan.checkPayload): every
 * item's time must parse, and every item needs at least one option. Returns an
 * error string or "".
 */
export const validatePlan = (data) => {
  const cats = Object.keys(data || {});
  for (let i = 0; i < cats.length; i += 1) {
    const items = (data[cats[i]] && data[cats[i]].items) || [];
    for (let j = 0; j < items.length; j += 1) {
      if (!isTimeValid(items[j].time)) {
        return `Check time on "${items[j].name}" — use h:mm am/pm.`;
      }
      if (!(items[j].options || []).length) {
        return `"${items[j].name}" has no options — add one or remove the section.`;
      }
    }
  }
  return "";
};

/**
 * The save body — bridge's updatePatientHealthPlan(publish) field-for-field.
 * `employee` is { id, name }.
 *
 * Built FRESH, never spread from the fetched plan: bridge's accepted body
 * carries EXACTLY this field set (verified against a live working curl from
 * bridge, 2026-07-24). The GET response carries more — `publishedAt` today,
 * and whatever the server adds next — and echoing unknown fields back makes
 * POST /v4/hp/{pid} a 400. Numeric `level` is likewise mandatory on create.
 */
export const buildSaveBody = (plan, publish, employee) => {
  const body = {
    level: levelNumber(plan.current_level),
    patientId: plan.patientId,
    current_level: plan.current_level,
    data: dataFilter(plan.data),
    published: publish,
    createdBy: employee.id,
    updatedBy: employee.id,
    updatedByName: employee.name,
    publishedBy: publish ? employee.name : null,
    publishedById: publish ? employee.id : null,
    createdAt: parseInt(plan.createdAt, 10) || Date.now(),
    updatedAt: new Date().getTime(),
  };
  if (plan.id != null) body.id = plan.id;
  return body;
};

/**
 * The version-history rows from the list + latest (deduped by id, desc).
 *
 * The list (GET /v4/hp/list/{pid}) answers { id, current_level } and nothing
 * else — no date, no author, not even `published` — so every row but the open
 * latest printed a blank line under its name (Nittin, 2026-10-07). `details`
 * is each version's own record (GET /v4/hp/{pid}/{hpId}, what "Open" loads),
 * keyed by id; the open latest is newer than any of them and wins.
 */
/* A version the SYSTEM made reads "Auto" where a person's name would be (Figma
   3187:6473). hp-automation stamps every plan it generates itself — the level
   generation and the old-plan migration — with publishedBy "Auto Generation"
   and no user behind it. */
const isAutoGenerated = (p) =>
  typeof (p && p.publishedBy) === "string" && p.publishedBy.trim().toLowerCase() === "auto generation";

export const toVersionRows = (list, latest, details) => {
  const all = [];
  const seen = new Set();
  [latest, ...(list || [])].forEach((p) => {
    if (!p || !p.id || seen.has(p.id)) return;
    seen.add(p.id);
    const v = p === latest ? p : { ...p, ...((details && details[p.id]) || {}) };
    all.push({
      id: v.id,
      version: levelNumber(v.current_level),
      published: Boolean(v.published),
      meta: [formatPlanDate(v.updatedAt || v.createdAt), v.updatedByName || (isAutoGenerated(v) ? "Auto" : "")]
        .filter(Boolean)
        .join(" · "),
    });
  });
  all.sort((a, b) => b.version - a.version);
  return all;
};

/* ---------- food-list flags on options (old bridge's "Not Allowed" / "Limited") ---------- */

const FOOD_FLAG_LISTS = [
  { key: "notAllowed", list: "notAllowedFoodList", label: "Not Allowed", tone: "bad" },
  { key: "limited", list: "limitedFoodList", label: "Limited", tone: "warn" },
];

const foodName = (s) => (typeof s === "string" ? s.trim().toLowerCase() : "");

/**
 * The patient's food list (GET /v1/foodlist/:pid/latest) indexed for flagging:
 * per flag list, the food names keyed by ingredient id (a food item IS an
 * ingredient — foodItemId is its ObjectID) and by lower-cased name. Missing or
 * null lists index as empty.
 */
export const foodListIndex = (foodList) =>
  FOOD_FLAG_LISTS.map((f) => {
    const rows = ((foodList && Array.isArray(foodList[f.list]) && foodList[f.list]) || []).filter(Boolean);
    const label = (r) => (typeof r.foodItemName === "string" && r.foodItemName.trim()) || "";
    // Go's zero ObjectID is "no id", never a real ingredient to match on.
    const realId = (id) => !!id && !/^0{24}$/.test(String(id));
    return {
      ...f,
      ids: new Map(rows.filter((r) => realId(r.foodItemId)).map((r) => [String(r.foodItemId), label(r)])),
      names: new Map(rows.filter((r) => foodName(r.foodItemName)).map((r) => [foodName(r.foodItemName), label(r)])),
    };
  });

/**
 * Old bridge's option tags (new-bridge health-plan Options.getFlagsFood): an
 * option is flagged "Not Allowed" / "Limited" when any of its recipes' KEY
 * ingredients is on that list of the patient's food list — the whole option, in
 * that order, both when both match. Matched by ingredient id, else by name
 * (bridge compared names exactly; case and surrounding space are ignored here,
 * and a nameless ingredient never matches by name — bridge's
 * `undefined === undefined` flagged every option while the list was loading).
 * Returns [{ key, label, tone, names: [matched food names] }].
 */
export const foodFlagsOf = (option, index) => {
  const recipes = (option && Array.isArray(option.recipes) && option.recipes) || [];
  const keys = [];
  recipes.forEach((r) => {
    ((r && Array.isArray(r.keyIngredients) && r.keyIngredients) || []).forEach((k) => {
      if (k) keys.push(k);
    });
  });
  if (!keys.length || !Array.isArray(index)) return [];
  return index
    .map((f) => {
      let matched = false;
      const names = [];
      keys.forEach((k) => {
        const idName = k.id != null ? f.ids.get(String(k.id)) : undefined;
        const n = foodName(k.name);
        const nameName = n ? f.names.get(n) : undefined;
        if (idName === undefined && nameName === undefined) return;
        matched = true;
        const shown = (typeof k.name === "string" && k.name.trim()) || idName || nameName;
        if (shown && !names.some((x) => foodName(x) === foodName(shown))) names.push(shown);
      });
      return matched ? { key: f.key, label: f.label, tone: f.tone, names } : null;
    })
    .filter(Boolean);
};
